import { BaseRecord, HttpError, useTranslate } from '@refinedev/core';
import { UseFormReturnType } from '@refinedev/react-hook-form';
import { FieldGroup, Label } from '@scalingworks/react-admin-ui';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import { getSdk, type Product, GetFulfillmentChannelsQuery } from '~/api';
import { MultiCollectionSelection, MultiModifierSelection } from '~/components';
import { FormItemProps } from '~/components/FormBuilder/props';
import { ProductVariantFormInput } from '~/components/VariantForm/props';
import { GQLClient } from '../gql-client';
import { toCamelCaseWord } from '~/resources/helpers';
import flatMap from 'lodash/flatMap';

export const formBuilder = ({
  product,
  formHook,
  setProductVariants,
  type = 'Product',
}: {
  product?: Product;
  formHook: UseFormReturnType<BaseRecord, HttpError, FieldValues, {}>;
  setProductVariants?: React.Dispatch<React.SetStateAction<ProductVariantFormInput[] | undefined>>;
  type?: 'Product' | 'Modifier';
}): FormItemProps[] => {
  const { name, description, enabled, assets, facetValues, modifierGroups } = product || {};
  const { watch, setValue, control } = formHook || {};
  const t = useTranslate();
  const placeholderType = t(`product.name.${[type.toLowerCase()]}`).toLowerCase();

  // Getting shipping data
  const [shippingData, setShippingData] =
    useState<GetFulfillmentChannelsQuery['getFulfillmentChannels']['items']>();
  const gqlClient = GQLClient?.getInstance();
  useEffect(() => {
    getSdk(gqlClient)
      ?.GetFulfillmentChannels()
      ?.then((res) => {
        setShippingData(res?.getFulfillmentChannels?.items);
      });
  }, []);

  // Setting default value
  useEffect(() => {
    setValue(
      'categories',
      facetValues?.map((subItem) => `${subItem?.id}-split-${subItem?.name}`)
    );
    setValue('customFields', product?.customFields);
  }, [product]);
  return [
    {
      name: 'name',
      title: t('product.form.name'),
      placeholder: t('product.placeholder.name', {
        type: placeholderType,
      }),
      defaultValue: name,
      required: true,
    },
    {
      name: 'description',
      title: t('product.form.description'),
      placeholder: t('product.placeholder.description', {
        type: placeholderType,
      }),
      defaultValue: description,
      multiline: true,
    },
    {
      name: 'images',
      title: t('product.form.images'),
      placeholder: t('product.placeholder.images'),
      hint: t('messages.acceptedFileTypes', { files: 'JPG, JPEG, PNG' }),
      defaultValue: !isEmpty(assets) ? [assets?.[0]?.source] : undefined,
      type: 'upload',
    },
    {
      name: 'basePrice',
      title: t('product.form.basePrice'),
      placeholder: '0.00',
      prefix: 'RM',
      type: 'number',
      willHide: true,
      required: true,
    },
    // @ts-ignore
    ...(type === 'Product'
      ? [
          {
            name: 'quantity',
            title: t('product.form.quantity'),
            placeholder: t('product.placeholder.quantity', {
              type: placeholderType,
            }),
            type: 'number',
            willHide: true,
            required: true,
          },
          {
            name: 'enable',
            title: t('product.form.enable'),
            type: 'switch',
            defaultValue: !isEmpty(product) ? enabled : true,
            hint: t('warnings.disableProduct'),
            onChange: !isEmpty(product)
              ? (val: boolean) => {
                  const mainVariants = watch('variants') as ProductVariantFormInput[];
                  mainVariants?.forEach((subItem, index) => {
                    setValue(`variants.${index}.enabled`, val);
                  });
                  setProductVariants &&
                    setProductVariants((prev) => {
                      const updatedVariants = [...(prev || [])] as ProductVariantFormInput[];
                      updatedVariants?.forEach((subItem, index) => {
                        updatedVariants[index]['enabled'] = val;
                      });
                      return updatedVariants;
                    });
                }
              : undefined,
          },
          {
            title: t('product.form.salesChannel'),
            name: 'shipping',
            type: 'checkbox',
            defaultValue: !isEmpty(product)
              ? product?.customFields?.fulfillChannels?.map((subItem) => subItem?.id)
              : undefined,
            options: shippingData?.map((res) => {
              return {
                label: t(`product.fulfillmentMethod.${res?.name?.toLowerCase()}`),
                value: res?.id,
              };
            }),
            required: true,
          },
          {
            title: t('product.form.categories'),
            render: () => {
              return (
                <Controller
                  name="categories"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  defaultValue={facetValues?.map((subItem) => {
                    return `${subItem?.id}-split-${subItem?.name}`;
                  })}
                  render={({ field: { onChange, value }, fieldState: { error } }) => {
                    return (
                      <FieldGroup className="my-4">
                        <Label>{t('product.form.categories')}</Label>
                        <div className="w-full">
                          <MultiCollectionSelection
                            isWithName
                            // required
                            initialOption={facetValues?.map((subItem) => {
                              return {
                                label: subItem?.name,
                                value: `${subItem?.id}-split-${subItem?.name}`,
                              };
                            })}
                            onSelect={onChange}
                            value={value}
                          />
                          {!isEmpty(error) && (
                            <div className="mt-2">
                              <p className="text-error-300 text-sm mt-3">
                                {t('product.errors.categoriesRequired')}
                              </p>
                            </div>
                          )}
                        </div>
                      </FieldGroup>
                    );
                  }}
                />
              );
            },
          },
          {
            title: t('product.form.modifiers'),
            render: () => {
              const allModifiers = flatMap(modifierGroups?.map((group) => group.modifiers)) || [];

              return (
                <Controller
                  name="modifiers"
                  control={control}
                  defaultValue={allModifiers?.map((mod) => {
                    return mod.id;
                  })}
                  render={({ field: { onChange, value }, fieldState: { error } }) => {
                    return (
                      <div className="w-full">
                        <MultiModifierSelection
                          isField
                          fieldLabel={t('product.form.modifiers')}
                          // isWithName
                          initialOption={allModifiers?.map((mod) => {
                            return {
                              label: mod?.name,
                              value: mod?.id,
                            };
                          })}
                          onSelect={onChange}
                          value={value}
                        />
                      </div>
                    );
                  }}
                />
              );
            },
          },
        ]
      : []),
  ];
};
